/**
 * 360 Cookware – Build Your Own Set
 * File: assets/bundle-builder.js
 *
 * CHANGES v2 (based on team feedback):
 * 1. All steps unlocked from the start — no order restriction
 * 2. Oven Sheets + Bakeware merged into one step
 * 3. Slow Cooker added as its own step
 * 4. 8 Qt Stock Pot no longer triggers slow cooker popup
 * 5. 4 cards per row with horizontal slider for overflow
 * 6. Multiple selections per step + multiples of same item (qty stepper)
 *
 * CHANGES v3 (bugfix):
 * 7. Fixed "stuck arrows" bug: Swiper instances were being created (in
 *    bundle-builder.liquid) against empty .swiper-wrapper elements before
 *    product cards existed, then only "update()"-d afterward. update()
 *    does not rebuild loop:true internals, so nav arrows desynced from
 *    the real slide count and appeared to freeze after any interaction.
 *    Fix: loadStepProducts() now calls window.BB_initSwipers(stepId),
 *    which destroys and recreates that step's Swiper instance after its
 *    real cards are injected.
 */

(function () {
  "use strict";

  /* ============================================================
     CONFIGURATION
     ============================================================ */
  const BB_CONFIG = {
    bundleProductHandle: "build-your-own-set",

    discountTiers: [
      { min: 2, pct: 10 },
      { min: 3, pct: 15 },
      { min: 4, pct: 20 },
      { min: 5, pct: 25 },
    ],

    slowCookerHandle: "slow-cooker-base",

    steps: [
      {
        id: 1,
        label: "Fry Pan",
        subtitle: "Your everyday go-to for eggs, searing, sautéing and quick meals.",
        products: [
          { handle: "stainless-steel-10-inch-fry-pan", bestseller: true },
          { handle: "stainless-steel-8-5-inch-fry-pan", bestseller: false },
          { handle: "stainless-steel-11-5-inch-fry-pan", bestseller: false },
          { handle: "stainless-steel-square-griddle", bestseller: false },
          { handle: "stainless-steel-7-inch-fry-pan", bestseller: false },
          {
            handle:
              "new-stainless-steel-10-inch-fry-pan-with-two-short-handles",
            bestseller: false,
          },
          { handle: "8-5-inch-fry-pan-with-side-handles", bestseller: false },
          {
            handle: "stainless-steel-11-5-inch-fry-pan-with-two-short-handles",
            bestseller: false,
          },
        ],
      },
      {
        id: 2,
        label: "Saucepan",
        subtitle: "Perfect for sauces, grains, vegetables, soups and everyday sides.",
        products: [
          {
            handle: "stainless-steel-3-quart-saucepan-with-cover",
            bestseller: true,
          },
          {
            handle: "stainless-steel-2-quart-saucepan-with-cover",
            bestseller: false,
          },
          {
            handle: "stainless-steel-1-quart-saucepan-with-cover-new-logo",
            bestseller: false,
          },
          { handle: "1-75-quart-saucepan", bestseller: false },
          {
            handle: "stainless-steel-8-quart-stockpot-with-cover",
            bestseller: false,
            isStockPot: false,
          },
          {
            handle: "stainless-steel-6-quart-gourmet-stockpot-with-cover",
            bestseller: false,
            isStockPot: true,
            isBackorder: true,
          },
          {
            handle: "stainless-steel-4-quart-stockpot-with-cover",
            bestseller: false,
            isStockPot: true,
          },
        ],
      },
      {
        id: 3,
        label: "Sauté Pan",
        subtitle: "Great for one-pan meals, browning, braising and recipes that need a little more room.",
        products: [
          {
            handle: "stainless-steel-3-5-quart-saute-pan-with-cover",
            bestseller: true,
          },
          {
            handle: "stainless-steel-3-5-quart-saute-pan-with-side-handles",
            bestseller: false,
          },
          {
            handle: "stainless-steel-2-3-quart-casserole-with-cover",
            bestseller: false,
            isStockPot: true,
          },
          { handle: "8-covered-fry-pan", bestseller: false },
          { handle: "stainless-steel-wok", bestseller: false },
        ],
      },
      {
        // New dedicated Slow Cooker step
        id: 4,
        label: "Slow Cooker",
        subtitle: "Perfect for hands-off meals, soups, stews, roasts and busy days when you want dinner ready when you are.",
        products: [
          {
            handle: "4-quart-stainless-steel-stock-pot-with-cover-slow-cooker",
            bestseller: true,
          },
          {
            handle: "6-quart-stainless-steel-stock-pot-with-cover-slow-cooker",
            bestseller: false,
            isBackorder: true,
          },
          {
            handle:
              "2-3-quart-stainless-steel-stock-pot-with-cover-slow-cooker",
            bestseller: false,
          },
          { handle: "slow-cooker-base", bestseller: false },
        ],
      },
      {
        // Merged: Oven Sheets + Bakeware
        id: 5,
        label: "Bakeware",
        subtitle: "For everything from sheet-pan dinners and roasting to cookies, breads and family favorites.",
        products: [
          { handle: "stainless-steel-jelly-roll-pan", bestseller: true },
          { handle: "stainless-steel-cookie-sheet-large", bestseller: false },
          { handle: "stainless-steel-cookie-sheet-small", bestseller: false },
          {
            handle: "9-x-13-stainless-steel-bake-roast-pan",
            bestseller: false,
          },
          { handle: "9x13-bake-roast-with-no-handles", bestseller: false },
          { handle: "3-1-roasting-pan", bestseller: false },
          { handle: "stainless-steel-mini-jelly-pan", bestseller: false },
          { handle: "loaf-pan-with-tab-handles", bestseller: false },
          { handle: "9-round-cake-pan", bestseller: false },
          { handle: "stainless-steel-loaf-pan", bestseller: false },
          { handle: "9-stainless-steel-pie-pan", bestseller: false },
          { handle: "9-round-stainless-steel-cake-pan", bestseller: false },
          { handle: "9-round-circle-baking-pan", bestseller: false },
        ],
      },
    ],
  };

  /* ============================================================
     STATE
     selections is now per-step array to support multiples:
     { stepId: [ { variantId, name, price, image, stepLabel, qty } ] }
     extras kept for slow cooker modal add-ons
     ============================================================ */
  const state = {
    selections: {}, // { stepId: [ selectionObj, ... ] }
    extras: [],
    activeStep: 1,
    productData: {},
    slowCookerShown: false,
    exitShown: false,
  };

  /* ============================================================
     UTILS
     ============================================================ */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return "$" + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function getDiscountPct(itemCount) {
    let pct = 0;
    for (const tier of BB_CONFIG.discountTiers) {
      if (itemCount >= tier.min) pct = tier.pct;
    }
    return pct;
  }

  function computeAdjustedDiscount(bundleDiscount, compareAtPrice, currentPrice) {
    if (compareAtPrice <= 0 || currentPrice <= 0) {
      return bundleDiscount;
    }

    if (compareAtPrice < currentPrice) {
      return bundleDiscount;
    }

    if (compareAtPrice === currentPrice) {
      return bundleDiscount;
    }

    const existingDiscount =
      ((compareAtPrice - currentPrice) / compareAtPrice) * 100;

    if (existingDiscount >= bundleDiscount) {
      return 0;
    }

    return bundleDiscount - existingDiscount;
  }

  // Total = sum of all qty across all steps + extras
  function getTotalItemCount() {
    let count = 0;
    Object.values(state.selections).forEach((arr) => {
      arr.forEach((s) => {
        count += s.qty;
      });
    });
    count += state.extras.length;
    return count;
  }

  function getOriginalTotal() {
    let total = 0;

    Object.values(state.selections).forEach((arr) => {
      arr.forEach((s) => {
        const itemPrice = s.compareAtPrice ?? s.price;
        console.log('')
        total += itemPrice * s.qty;
      });
    });

    state.extras.forEach((e) => {
      const extraPrice = e.compareAtPrice ?? e.price;
      total += extraPrice;
    });

    return total;
  }

  function getSelectionQty(stepId, variantId) {
    const arr = state.selections[stepId] || [];
    const sel = arr.find((s) => s.variantId === variantId);
    return sel ? sel.qty : 0;
  }

  function getStepTotalQty(stepId) {
    return (state.selections[stepId] || []).reduce((sum, s) => sum + s.qty, 0);
  }

  function scrollToStep(stepId) {
    const el = document.getElementById("bb-step-" + stepId);
    if (!el) return;
    const offset = 90;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }

  /* ============================================================
     FETCH
     ============================================================ */
  async function fetchProduct(handle) {
    if (state.productData[handle]) return state.productData[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error("Not found: " + handle);
      const data = await res.json();
      state.productData[handle] = data;
      return data;
    } catch (e) {
      console.warn("[BundleBuilder] Could not fetch product:", handle, e);
      return null;
    }
  }

  /* ============================================================
     RENDER PRODUCT CARD
     Now shows qty stepper instead of single-select checkmark
     ============================================================ */
  function renderProductCard(product, stepId, isBestseller, isStockPot, isBackorder) {
    if (!product) return null;

    const variant = product.variants[0];
    const price = variant.price;
    const comparePrice = variant.compare_at_price;
    const image = product.images[0] ? product.images[0] : null;
    const qty = getSelectionQty(stepId, variant.id);
    const isSelected = qty > 0;

    const card = document.createElement("div");
    card.className =
      "bb-product-card swiper-slide" + (isSelected ? " is-selected" : "");
    card.dataset.handle = product.handle;
    card.dataset.stepId = stepId;
    card.dataset.variantId = variant.id;
    card.dataset.price = price;
    card.dataset.stockPot = isStockPot ? "1" : "0";

    card.innerHTML = `
      ${isBestseller ? '<span class="bb-product-card__badge">Bestseller</span>' : ""}
      ${isBackorder ? '<span class="bb-product-card__badge isbackorder">Backorder</span>' : ""}
      <div class="bb-product-card__img-wrap">
        ${
          image
            ? `<img src="${image}" alt="${product.title}" loading="lazy">`
            : `<svg class="bb-product-card__img-placeholder" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="currentColor" opacity=".05"/><path d="M16 32l6-8 4 6 3-4 5 6H16z" fill="currentColor" opacity=".2"/></svg>`
        }
        ${
          document.getElementById("bundle-builder")?.dataset.enableQuickView ===
          "true"
            ? `<div class="card-quick-view">
                <button type="button" class="card-quick-view__btn" data-quick-view data-product-url="/products/${product.handle}" aria-haspopup="dialog" aria-label="Quick view ${product.title}">
                  Quick View
                </button>
              </div>`
            : ""
        }
      </div>
      <p class="bb-product-card__name">${product.title}</p>
      <div style="display:flex;align-items:baseline;gap:0">
        <span class="bb-product-card__price">${formatMoney(price)}</span>
        ${
          comparePrice && comparePrice > price
            ? `<span class="bb-product-card__price-original">${formatMoney(comparePrice)}</span>`
            : ""
        }
      </div>
      <div class="bb-product-card__stepper ${isSelected ? "is-visible" : ""}">
        <button class="bb-stepper__btn bb-stepper__btn--minus" aria-label="Remove one">
          <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
            <path d="M1 1h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <span class="bb-stepper__qty">${qty}</span>
        <button class="bb-stepper__btn bb-stepper__btn--plus" aria-label="Add one">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M5 1v8M1 5h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;

    // Click card body = add 1 (only if stepper not yet shown)
    card.addEventListener("click", (e) => {
      if (e.target.closest(".bb-product-card__stepper")) return;
      if (e.target.closest("[data-quick-view]")) return;
      if (document.body.classList.contains("quick-view-open")) return;
      const qvModal = document.getElementById("QuickViewModal");
      if (qvModal && qvModal.hasAttribute("open")) return;
      handleQtyChange(card, product, variant, stepId, isStockPot, 1);
    });

    card
      .querySelector(".bb-stepper__btn--minus")
      .addEventListener("click", (e) => {
        e.stopPropagation();
        handleQtyChange(card, product, variant, stepId, isStockPot, -1);
      });

    card
      .querySelector(".bb-stepper__btn--plus")
      .addEventListener("click", (e) => {
        e.stopPropagation();
        handleQtyChange(card, product, variant, stepId, isStockPot, 1);
      });

    return card;
  }

  /* ============================================================
     HANDLE QTY CHANGE
     ============================================================ */
  function handleQtyChange(card, product, variant, stepId, isStockPot, delta) {
    const stepConfig = BB_CONFIG.steps.find((s) => s.id === stepId);
    if (!state.selections[stepId]) state.selections[stepId] = [];

    const arr = state.selections[stepId];
    const existing = arr.find((s) => s.variantId === variant.id);
    const newQty = Math.max(0, (existing ? existing.qty : 0) + delta);

    if (newQty === 0) {
      state.selections[stepId] = arr.filter((s) => s.variantId !== variant.id);
    } else if (existing) {
      existing.qty = newQty;
    } else {
      arr.push({
        stepId,
        stepLabel: stepConfig ? stepConfig.label : "",
        variantId: variant.id,
        handle: product.handle,
        name: product.title,
        price: variant.price,
        compareAtPrice: variant.compare_at_price,
        image: product.images[0] ? product.images[0] : null,
        qty: newQty,
      });
    }

    // Update card UI
    const finalQty = getSelectionQty(stepId, variant.id);
    const stepperEl = card.querySelector(".bb-product-card__stepper");
    const qtyEl = card.querySelector(".bb-stepper__qty");
    qtyEl.textContent = finalQty;
    card.classList.toggle("is-selected", finalQty > 0);
    stepperEl.classList.toggle("is-visible", finalQty > 0);

    // Slow cooker upsell — only 4qt & 6qt
    if (isStockPot && delta > 0 && !state.slowCookerShown) {
      state.slowCookerShown = true;
      setTimeout(() => showModal("bb-slow-cooker-modal"), 350);
    }

    updateStepStatus(stepId, getStepTotalQty(stepId) > 0);
    updateUI();
  }

  /* ============================================================
     STEP MANAGEMENT
     ============================================================ */
  function updateStepStatus(stepId, hasSelections) {
    const stepEl = document.getElementById("bb-step-" + stepId);
    if (!stepEl) return;
    const badge = stepEl.querySelector(".bb-step__status-badge");
    if (!badge) return;
    const qty = getStepTotalQty(stepId);
    if (hasSelections && qty > 0) {
      badge.textContent = qty === 1 ? "Selected ✓" : `${qty} selected ✓`;
      badge.className = "bb-step__status-badge bb-step__status-badge--selected";
      stepEl.classList.add("bb-step--complete");
    } else {
      badge.textContent = "Not Selected";
      badge.className = "bb-step__status-badge bb-step__status-badge--pending";
      stepEl.classList.remove("bb-step--complete");
    }
  }

  function updateStepDots() {
    BB_CONFIG.steps.forEach((step) => {
      const dot = document.querySelector(
        `.bb-step-dot[data-step="${step.id}"]`,
      );
      if (!dot) return;
      dot.classList.remove("is-active", "is-complete");
      if (getStepTotalQty(step.id) > 0) {
        dot.classList.add("is-complete");
      } else if (step.id === state.activeStep) {
        dot.classList.add("is-active");
      }
    });
  }

  /* ============================================================
     UPDATE ALL UI
     ============================================================ */
  function updateUI() {
    const count = getTotalItemCount();
    const pct = getDiscountPct(count);let originalTotal = 0;
    let finalTotal = 0;

    Object.values(state.selections).forEach((arr) => {
      arr.forEach((s) => {
        const compareAt =
          s.compareAtPrice ?? s.price;

        const current =
          s.price;

        const effectiveDiscount =
          computeAdjustedDiscount(
            pct,
            compareAt,
            current
          );

        originalTotal += compareAt * s.qty;

        finalTotal += Number(
          (
            current *
            (1 - effectiveDiscount / 100) *
            s.qty
          ).toFixed(6)
        );
      });
    });

    state.extras.forEach((e) => {
      const compareAt =
        e.compareAtPrice ?? e.price;

      const current =
        e.price;

      const effectiveDiscount =
        computeAdjustedDiscount(
          pct,
          compareAt,
          current
        );

      originalTotal += compareAt;

      finalTotal += Number(
        (
          current *
          (1 - effectiveDiscount / 100)
        ).toFixed(6)
      );
    });

    const discountAmount =
      originalTotal - finalTotal;

    console.log('====',{
        originalTotal,
        finalTotal,
        discountAmount,
      });

    const hasItems = count > 0;

    // Update discount tier pills in progress bar
    document.querySelectorAll(".bb-tier-dot").forEach((dot) => {
      const min = parseInt(dot.dataset.min);
      dot.classList.remove("is-active", "is-passed");
      if (count >= min) {
        dot.classList.add("is-passed");
      }
    });
    // Mark the current active tier
    const activeTierDot = [...document.querySelectorAll(".bb-tier-dot")]
      .reverse()
      .find((dot) => count >= parseInt(dot.dataset.min));
    if (activeTierDot) {
      activeTierDot.classList.remove("is-passed");
      activeTierDot.classList.add("is-active");
    }

    document.getElementById("bb-item-count").textContent = count;

    const discountEl = document.getElementById("bb-discount-value");
    discountEl.textContent = pct > 0 ? pct + "% Off" : "—";
    discountEl.style.color = pct > 0 ? "var(--bb-primary-dk)" : "var(--bb-mid)";

    document.querySelectorAll(".bb-tier").forEach((tier) => {
      const min = parseInt(tier.dataset.min);
      tier.classList.toggle(
        "is-active",
        count >= min && min === getActiveTierMin(count),
      );
    });

    const sidebarDiscountText = document.getElementById(
      "bb-sidebar-discount-text",
    );
    if (pct > 0) {
      sidebarDiscountText.textContent = `${pct}% discount applied!`;
    } else if (count === 1) {
      sidebarDiscountText.textContent = "Add 1 more item to save 10%";
    } else {
      sidebarDiscountText.textContent = "Add 2+ items to save";
    }

    renderSidebarItems();

    const totalsEl = document.getElementById("bb-sidebar-totals");
    if (hasItems) {
      totalsEl.style.display = "flex";
      document.getElementById("bb-original-total").textContent =
        formatMoney(originalTotal);
      document.getElementById("bb-discount-label").textContent =
        `Discount (${pct}%)`;
      document.getElementById("bb-discount-amount").textContent =
        "-" + formatMoney(discountAmount);
      document.getElementById("bb-final-total").textContent =
        formatMoney(finalTotal);
    } else {
      totalsEl.style.display = "none";
    }

    const btnTotal = document.getElementById("bb-btn-total");
    if (btnTotal)
      btnTotal.textContent = hasItems ? "· " + formatMoney(finalTotal) : "";

    ["bb-sticky-atc", "bb-sidebar-atc", "bb-main-atc"].forEach((id) => {
      const btn = document.getElementById(id);
      if (btn) btn.disabled = !hasItems;
    });

    updateStepDots();
  }

  function getActiveTierMin(count) {
    let active = 0;
    for (const tier of BB_CONFIG.discountTiers) {
      if (count >= tier.min) active = tier.min;
    }
    return active;
  }

  function renderSidebarItems() {
    const list = document.getElementById("bb-sidebar-list");
    list.innerHTML = "";

    // Flatten all step selections + extras
    const allItems = [];
    Object.values(state.selections).forEach((arr) => {
      arr.forEach((s) => {
        if (s.qty > 0) allItems.push(s);
      });
    });
    state.extras.forEach((e) => allItems.push(e));

    if (allItems.length === 0) {
      list.innerHTML = `
        <li class="bb-sidebar__empty-state">
          <div class="bb-sidebar__empty-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="1.5" stroke-dasharray="4 3"/><path d="M24 16v8M24 28v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </div>
          <p>Start selecting items to build your set</p>
        </li>`;
      return;
    }

    allItems.forEach((item, idx) => {
      const isExtra = !!item.isExtra;
      const li = document.createElement("li");
      li.className = "bb-sidebar-item";
      li.innerHTML = `
        ${
          item.image
            ? `<img class="bb-sidebar-item__img" src="${item.image}" alt="${item.name}">`
            : `<div class="bb-sidebar-item__img" style="background:var(--bb-light)"></div>`
        }
        <div class="bb-sidebar-item__info">
          <p class="bb-sidebar-item__name">${item.name}</p>
          <p class="bb-sidebar-item__step">${item.stepLabel || "Add-on"}</p>
        </div>
        ${
          !isExtra
            ? `
        <div class="bb-sidebar-item__qty-ctrl">
          <button class="bb-sidebar-qty__btn" data-action="minus" data-step="${item.stepId}" data-variant="${item.variantId}">−</button>
          <span class="bb-sidebar-qty__num">${item.qty}</span>
          <button class="bb-sidebar-qty__btn" data-action="plus"  data-step="${item.stepId}" data-variant="${item.variantId}">+</button>
        </div>`
            : ""
        }
        <span class="bb-sidebar-item__price">${formatMoney(item.price * (item.qty || 1))}</span>
        <button class="bb-sidebar-item__remove" aria-label="Remove ${item.name}"
          data-step="${item.stepId || ""}" data-variant="${item.variantId || ""}" data-extra="${isExtra ? idx : ""}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>`;

      // Sidebar qty buttons
      li.querySelectorAll(".bb-sidebar-qty__btn").forEach((btn) => {
        btn.addEventListener("click", () => {
          const sid = parseInt(btn.dataset.step);
          const vid = parseInt(btn.dataset.variant);
          const action = btn.dataset.action;
          const arr = state.selections[sid] || [];
          const sel = arr.find((s) => s.variantId === vid);
          if (!sel) return;

          sel.qty += action === "plus" ? 1 : -1;

          if (sel.qty <= 0) {
            state.selections[sid] = arr.filter((s) => s.variantId !== vid);
            // Reset card in grid
            const grid = document.querySelector(`[data-step-grid="${sid}"]`);
            if (grid) {
              grid.querySelectorAll(".bb-product-card").forEach((c) => {
                if (parseInt(c.dataset.variantId) === vid) {
                  c.classList.remove("is-selected");
                  c.querySelector(".bb-stepper__qty").textContent = 0;
                  c.querySelector(".bb-product-card__stepper").classList.remove(
                    "is-visible",
                  );
                }
              });
            }
          } else {
            // Update card stepper qty
            const grid = document.querySelector(`[data-step-grid="${sid}"]`);
            if (grid) {
              grid.querySelectorAll(".bb-product-card").forEach((c) => {
                if (parseInt(c.dataset.variantId) === vid) {
                  c.querySelector(".bb-stepper__qty").textContent = sel.qty;
                }
              });
            }
          }
          updateStepStatus(sid, getStepTotalQty(sid) > 0);
          updateUI();
        });
      });

      // Remove button
      li.querySelector(".bb-sidebar-item__remove").addEventListener(
        "click",
        (e) => {
          const sid = parseInt(e.currentTarget.dataset.step);
          const vid = parseInt(e.currentTarget.dataset.variant);
          if (isExtra) {
            state.extras.splice(idx, 1);
          } else {
            state.selections[sid] = (state.selections[sid] || []).filter(
              (s) => s.variantId !== vid,
            );
            const grid = document.querySelector(`[data-step-grid="${sid}"]`);
            if (grid) {
              grid.querySelectorAll(".bb-product-card").forEach((c) => {
                if (parseInt(c.dataset.variantId) === vid) {
                  c.classList.remove("is-selected");
                  c.querySelector(".bb-stepper__qty").textContent = 0;
                  c.querySelector(".bb-product-card__stepper").classList.remove(
                    "is-visible",
                  );
                }
              });
            }
            updateStepStatus(sid, getStepTotalQty(sid) > 0);
          }
          updateUI();
        },
      );

      list.appendChild(li);
    });
  }

  /* ============================================================
     ADD TO CART
     ============================================================ */
  async function addBundleToCart() {
    const count = getTotalItemCount();
    const pct = getDiscountPct(count);

    if (pct < 25) {
      const itemsAway = 5 - count;
      if (itemsAway > 0) {
        document.getElementById("bb-items-away").textContent =
          itemsAway === 1 ? "1 item" : itemsAway + " items";
        showModal("bb-discount-reminder-modal");
        return;
      }
    }

    await doAddToCart();
  }

  async function doAddToCart() {
    const allItems = [];
    Object.values(state.selections).forEach((arr) => {
      arr.forEach((s) => {
        if (s.qty > 0) allItems.push(s);
      });
    });
    state.extras.forEach((e) => allItems.push({ ...e, qty: 1 }));

    if (allItems.length === 0) return;
    const bundleId = `byob-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

    const pct = getDiscountPct(getTotalItemCount());
    const items = allItems.map((item) => ({
      id: item.variantId,
      quantity: item.qty || 1,
      properties: {
        _bundle: "byob",
        _bundleId: bundleId,
        _bundleSize: getTotalItemCount(),
        _bundleDiscount: pct > 0 ? `${pct}` : "0",
        _currentPrice: item.price,
        _compareAtPrice: item.compareAtPrice
          ? `${item.compareAtPrice}`
          : "",
      },
    }));

    ["bb-sticky-atc", "bb-sidebar-atc", "bb-main-atc"].forEach((id) => {
      const btn = document.getElementById(id);
      if (btn) {
        btn.disabled = true;
        btn.textContent = "Adding...";
      }
    });

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error("Cart error");
      window.location.href = "/cart";
    } catch (err) {
      console.error("[BundleBuilder] Cart error:", err);
      alert("Something went wrong adding to cart. Please try again.");
      ["bb-sticky-atc", "bb-sidebar-atc", "bb-main-atc"].forEach((id) => {
        const btn = document.getElementById(id);
        if (btn) {
          btn.disabled = false;
          btn.textContent = "Add Bundle to Cart";
        }
      });
    }
  }

  /* ============================================================
     MODALS
     ============================================================ */
  function showModal(id) {
    const modal = document.getElementById(id);
    if (modal) modal.hidden = false;
    document.body.style.overflow = "hidden";
  }

  function hideModal(id) {
    const modal = document.getElementById(id);
    if (modal) modal.hidden = true;
    document.body.style.overflow = "";
  }

  function hideAllModals() {
    document.querySelectorAll(".bb-modal").forEach((m) => {
      m.hidden = true;
    });
    document.body.style.overflow = "";
  }

  document.querySelectorAll(".bb-modal__backdrop").forEach((bd) => {
    bd.addEventListener("click", hideAllModals);
  });

  document
    .getElementById("bb-slow-cooker-skip")
    ?.addEventListener("click", () => hideModal("bb-slow-cooker-modal"));
  document
    .getElementById("bb-slow-cooker-close")
    ?.addEventListener("click", () => hideModal("bb-slow-cooker-modal"));
  document
    .getElementById("bb-slow-cooker-add")
    ?.addEventListener("click", async () => {
      hideModal("bb-slow-cooker-modal");
      const product = await fetchProduct(BB_CONFIG.slowCookerHandle);
      if (product) {
        const variant = product.variants[0];
        state.extras.push({
          variantId: variant.id,
          name: product.title,
          price: variant.price,
          compareAtPrice: variant.compare_at_price,
          image: product.images[0] ? product.images[0] : null,
          stepLabel: "Add-on",
          isExtra: true,
        });
        updateUI();
      }
    });

  document
    .getElementById("bb-reminder-checkout")
    ?.addEventListener("click", () => {
      hideModal("bb-discount-reminder-modal");
      doAddToCart();
    });
  document
    .getElementById("bb-reminder-keep-shopping")
    ?.addEventListener("click", () => {
      hideModal("bb-discount-reminder-modal");
    });
  document.getElementById("bb-exit-complete")?.addEventListener("click", () => {
    hideModal("bb-exit-intent-modal");
  });

  /* ============================================================
     EXIT INTENT
     ============================================================ */
  document.addEventListener("mouseleave", (e) => {
    if (e.clientY <= 0 && !state.exitShown && getTotalItemCount() > 0) {
      state.exitShown = true;
      showModal("bb-exit-intent-modal");
    }
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden && !state.exitShown && getTotalItemCount() > 0) {
      state.exitShown = true;
    }
  });

  /* ============================================================
     ATC BUTTONS
     ============================================================ */
  ["bb-sticky-atc", "bb-sidebar-atc", "bb-main-atc"].forEach((id) => {
    document.getElementById(id)?.addEventListener("click", addBundleToCart);
  });

  /* ============================================================
     INITIALIZE — all steps unlocked, products loaded in parallel
     ============================================================ */
  async function loadStepProducts(stepConfig) {
    const grid = document.querySelector(`[data-step-grid="${stepConfig.id}"]`);
    if (!grid) return;

    // Skeleton placeholders
    grid.innerHTML = Array(stepConfig.products.length)
      .fill(
        `
    <div class="bb-product-card swiper-slide" style="pointer-events:none">
      <div class="bb-product-card__img-wrap"><div class="bb-skeleton" style="width:100%;height:100%"></div></div>
      <div class="bb-skeleton" style="height:14px;width:80%;margin-top:8px"></div>
      <div class="bb-skeleton" style="height:12px;width:40%;margin-top:6px"></div>
    </div>`,
      )
      .join("");

    const fetched = await Promise.all(
      stepConfig.products.map((p) => fetchProduct(p.handle)),
    );

    grid.innerHTML = "";
    fetched.forEach((product, i) => {
      if (!product) return;
      const cfg = stepConfig.products[i];
      const card = renderProductCard(
        product,
        stepConfig.id,
        cfg.bestseller,
        cfg.isStockPot,
        cfg.isBackorder,
      );
      if (card) grid.appendChild(card);
    });

    // FIX: real cards now exist in the DOM for this step, but a plain
    // swiper.update() does not rebuild loop:true internals (duplicated
    // slides / isBeginning-isEnd state), which is what left the nav
    // arrows "stuck" after any later interaction. Destroy + recreate
    // this step's Swiper instance instead, via the helper exposed by
    // bundle-builder.liquid.
    if (window.BB_initSwipers) {
      window.BB_initSwipers(stepConfig.id);
    } else {
      // Fallback in case the liquid section script hasn't loaded yet —
      // just try a normal update so we don't hard-fail.
      const swiperEl = document.querySelector(
        `.bb-step#bb-step-${stepConfig.id} .mySwiper`,
      );
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.update();
      }
    }
  }

  async function init() {
    state.activeStep = 1;

    // All steps unlocked from the start
    BB_CONFIG.steps.forEach((step) => {
      const el = document.getElementById("bb-step-" + step.id);
      if (el) el.classList.remove("bb-step--locked");
    });

    const step1El = document.getElementById("bb-step-1");
    if (step1El) step1El.classList.add("bb-step--active");

    //updateStepDots();
    updateUI();

    // Load all steps in parallel
    await Promise.all(BB_CONFIG.steps.map((step) => loadStepProducts(step)));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();